import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';

const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || '/contact.php';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    service_interests: [], // Changed to array for multiple services
    description: '',
    privacy_consent: false,
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    'Sandėliavimas',
    'Krovos darbai',
    'Prekių paruošimas',
    'Užsakymų valdymas',
    'Apskaita ir kontrolė',
    'Logistikos organizavimas',
    '3PL logistika',
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      service_interests: prev.service_interests.includes(service)
        ? prev.service_interests.filter(s => s !== service)
        : [...prev.service_interests, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Convert array to comma-separated string for backend
      const submitData = {
        ...formData,
        service_interest: formData.service_interests.join(', ') || 'Nenurodyta'
      };

      const response = await axios.post(CONTACT_ENDPOINT, submitData, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: response.data.message
        });
        
        // Reset form
        setFormData({
          company_name: '',
          contact_person: '',
          email: '',
          phone: '',
          service_interests: [],
          description: '',
          privacy_consent: false,
        });

        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setStatus({ type: '', message: '' });
        }, 3000);
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Įvyko klaida. Bandykite dar kartą.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="contact-modal">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="modal-close-button"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Modal header */}
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Gauti pasiūlymą</h2>
            <p className="text-gray-600">Užpildykite formą ir mes netrukus su jumis susisieksime</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Company Name */}
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                Įmonės pavadinimas *
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="UAB Pavyzdys"
                data-testid="modal-input-company-name"
              />
            </div>

            {/* Contact Person */}
            <div>
              <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-1">
                Kontaktinis asmuo *
              </label>
              <input
                type="text"
                id="contact_person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Vardas Pavardė"
                data-testid="modal-input-contact-person"
              />
            </div>

            {/* Email and Phone in Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  El. paštas *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="info@imone.lt"
                  data-testid="modal-input-email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonas
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="+370 600 00000"
                  data-testid="modal-input-phone"
                />
              </div>
            </div>

            {/* Service Interests - Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dominančios paslaugos
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {serviceOptions.map((service, index) => (
                  <label
                    key={index}
                    className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.service_interests.includes(service)}
                      onChange={() => handleServiceChange(service)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      data-testid={`modal-checkbox-service-${index}`}
                    />
                    <span className="text-sm text-gray-700">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Aprašymas
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                placeholder="Aprašykite savo poreikius: paletių skaičius, SKU, užsakymų kiekiai ir kt."
                data-testid="modal-textarea-description"
              ></textarea>
            </div>

            {/* Privacy Consent */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="privacy_consent"
                name="privacy_consent"
                checked={formData.privacy_consent}
                onChange={handleChange}
                required
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                data-testid="modal-checkbox-privacy"
              />
              <label htmlFor="privacy_consent" className="ml-2 text-sm text-gray-600">
                Sutinku su{' '}
                <a href="/privatumo-politika" target="_blank" className="text-blue-600 hover:underline">
                  privatumo politika
                </a>{' '}
                *
              </label>
            </div>

            {/* Status Message */}
            {status.message && (
              <div
                className={`flex items-center space-x-2 p-3 rounded-lg ${
                  status.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
                data-testid="modal-form-status-message"
              >
                {status.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                )}
                <span className="text-sm">{status.message}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              data-testid="modal-submit-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Siunčiama...</span>
                </>
              ) : (
                <span>Siųsti užklausą</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
